import {
  Box,
  Button,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import {
  OrganizationNiche,
  OrganizationProfile,
  OrganizationVaProfile,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import { TemplateMethod } from "features/Influencer/ContactList/schema";
import { isPage } from "utils/general";
import { getSource } from "utils/tracking";
import { useTracking } from "utils/useTracking";

import { SaveOrgVaProfileFunc, StepType, TemplateScreen } from "../../schema";
import TemplateCreateDialog from "./TemplateCreateDialog";

interface Props {
  organizationVaProfile: OrganizationVaProfile | null;
  saveOrgVaProfile: SaveOrgVaProfileFunc;
  organizationProfile: OrganizationProfile | null;
  steps: StepType[];
  setSteps: Dispatch<SetStateAction<StepType[]>>;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  moveToNextPage?: (updatedNiche?: OrganizationNiche) => void;
}

const StepsToPickTemplate = ({
  organizationVaProfile,
  saveOrgVaProfile,
  organizationProfile,
  activeStep,
  setActiveStep,
  steps,
  setSteps,
  moveToNextPage,
}: Props) => {
  const source = getSource();
  const { trackVaStep } = useTracking();

  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [screen, setScreen] = useState<TemplateScreen>(TemplateScreen.method);
  const [nicheBeingEdit, setNicheBeingEdit] = useState<
    OrganizationNiche | undefined
  >(undefined);

  const { outreachTemplates } = useContext(OutreachTemplatesContext);

  const handleSubmitMethod = (method: TemplateMethod) => {
    trackVaStep(routes.templatesMethodSelfServeOnboarding, source, true);
    if (method === TemplateMethod.use_bento_ai) {
      setScreen(TemplateScreen.bento_ai);
    } else {
      setScreen(TemplateScreen.from_scratch);
    }
  };

  const handleEdit = (niche: OrganizationNiche | undefined) => {
    setOpenTemplateDialog(true);
    setScreen(TemplateScreen.from_scratch);
    setNicheBeingEdit(niche);
  };

  const handleNextStep = (updatedNiche?: OrganizationNiche) => {
    let completed = false;
    if (updatedNiche) {
      setSteps((prevSteps) => {
        const newSteps = prevSteps.map((step) =>
          step.niche?.id === updatedNiche.id
            ? { ...step, niche: updatedNiche }
            : step,
        );
        if (isPage(routes.vaBundled)) {
          completed = newSteps.every(
            (step) => step.niche?.templates && step.niche.templates.length > 0,
          );
        }
        return newSteps;
      });
    } else {
      // Fallback templates have been set
      completed = true;
    }
    setOpenTemplateDialog(false);
    const indexOfNiche = steps?.findIndex(
      (step) => step.niche?.id === updatedNiche?.id,
    );
    if (indexOfNiche >= activeStep) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
    if (completed && moveToNextPage) {
      moveToNextPage();
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: 600 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index: number) => (
            <Step key={step.label}>
              <StepLabel>
                {step.label}{" "}
                {step?.niche?.templates &&
                  step?.niche?.templates?.length > 0 && (
                    <Button
                      color="secondary"
                      onClick={() => handleEdit(step.niche)}
                      size="small"
                      sx={{ textTransform: "none", fontSize: 10 }}
                    >
                      Edit{" "}
                      <Box
                        sx={{ ml: 1 }}
                        component="i"
                        className="fa-regular fa-edit"
                      />
                    </Button>
                  )}
              </StepLabel>

              <StepContent>
                <Typography gutterBottom>{step.description}</Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (outreachTemplates?.length === 0) {
                        setScreen(TemplateScreen.method);
                      } else {
                        setScreen(TemplateScreen.from_scratch);
                      }
                      setOpenTemplateDialog(true);
                      setNicheBeingEdit(step.niche);
                    }}
                    sx={{ textTransform: "none" }}
                    disableElevation
                    size="small"
                  >
                    Pick Templates
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      <TemplateCreateDialog
        openTemplateDialog={openTemplateDialog}
        setOpenTemplateDialog={setOpenTemplateDialog}
        handleSubmitMethod={handleSubmitMethod}
        niche={nicheBeingEdit}
        screen={screen}
        setScreen={setScreen}
        organizationVaProfile={organizationVaProfile}
        saveOrgVaProfile={saveOrgVaProfile}
        organizationProfile={organizationProfile}
        handleNextStep={handleNextStep}
      />
    </>
  );
};

export default StepsToPickTemplate;
